import React, { useEffect } from 'react';
import { Form, Row, Col, Input, InputNumber, Card, Divider, Tooltip, Alert } from 'antd';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import '../../../../assets/scss/components/OfflineBooking/FinancialDetailsForm.scss';

const FinancialDetailsForm = ({ 
  form,
  calculatedValues = {
    hostDiscountValue: 0,
    bookingAfterHostDiscount: 0,
    msDiscountValue: 0,
    bookingAfterAllDiscounts: 0,
    finalBookingValue: 0,
    baseCommission: 0,
    paymentToHost: 0,
    ccChargeValue: 0,
    finalCommissionToMS: 0,
    savingsBreakdown: []
  }, 
  spaceDetails,
  selectedChargingOption,
  editMode = false
}) => {
  // Get breakdown from calculatedValues
  const savingsBreakdown = calculatedValues.savingsBreakdown || [];
  
  // Separate discounts by type
  const hostDiscounts = savingsBreakdown.filter(s => 
    (s.name || '').toLowerCase().includes('host')
  );
  const msDiscounts = savingsBreakdown.filter(s => 
    (s.name || '').toLowerCase().includes('deal') || 
    (s.name || '').toLowerCase().includes('ms')
  );
  
  // Calculate totals
  const totalHostDiscountValue = hostDiscounts.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);
  const totalMsDiscountValue = msDiscounts.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);
  
  // Check if discounts are pre-configured from space (should be disabled)
  // Check the SPACE DETAILS directly, not the calculated breakdown
  // This prevents the field from becoming disabled when user manually enters a value
  const hasSpaceHostDiscount = React.useMemo(() => {
    if (!spaceDetails) return false;
    
    const selectedChargingTypeValue = form.getFieldValue('selectedChargingType');
    const eventData = spaceDetails.events && spaceDetails.events.length > 0 ? spaceDetails.events[0] : null;
    
    // Check chargings array
    if (eventData && eventData.chargings && Array.isArray(eventData.chargings)) {
      let matchingCharging = null;
      if (selectedChargingTypeValue) {
        matchingCharging = eventData.chargings.find(ch => 
          ch.chargingType?.id === selectedChargingTypeValue ||
          ch.chargingType?.name?.toLowerCase() === selectedChargingTypeValue.toLowerCase()
        );
      }
      if (!matchingCharging && eventData.chargings.length > 0) {
        matchingCharging = eventData.chargings[0];
      }
      
      if (matchingCharging && matchingCharging.discounts && Array.isArray(matchingCharging.discounts)) {
        return matchingCharging.discounts.some(d => d.id === '5e7cb0aeaac4f142d8075f86'); // HOST_DISCOUNT ID
      }
    }
    
    // Check spaceSavings array
    if (eventData && eventData.spaceSavings && Array.isArray(eventData.spaceSavings)) {
      const matchingSavings = eventData.spaceSavings.find(ss => {
        const chargingType = ss.chargingType?.toLowerCase() || '';
        return chargingType === (selectedChargingTypeValue || '').toLowerCase();
      });
      
      if (matchingSavings && matchingSavings.savings && Array.isArray(matchingSavings.savings)) {
        return matchingSavings.savings.some(savingItem => {
          const saving = savingItem.saving;
          return saving && saving.name === 'Host Discount' && saving.amountType === 'PERCENTAGE';
        });
      }
    }
    
    return false;
  }, [spaceDetails, form]);
  
  const hasSpaceMsDiscount = React.useMemo(() => {
    if (!spaceDetails) return false;
    
    const selectedChargingTypeValue = form.getFieldValue('selectedChargingType');
    const eventData = spaceDetails.events && spaceDetails.events.length > 0 ? spaceDetails.events[0] : null;
    
    // Check chargings array
    if (eventData && eventData.chargings && Array.isArray(eventData.chargings)) {
      let matchingCharging = null;
      if (selectedChargingTypeValue) {
        matchingCharging = eventData.chargings.find(ch => 
          ch.chargingType?.id === selectedChargingTypeValue ||
          ch.chargingType?.name?.toLowerCase() === selectedChargingTypeValue.toLowerCase()
        );
      }
      if (!matchingCharging && eventData.chargings.length > 0) {
        matchingCharging = eventData.chargings[0];
      }
      
      if (matchingCharging && matchingCharging.discounts && Array.isArray(matchingCharging.discounts)) {
        return matchingCharging.discounts.some(d => d.id === '5e7cb0aeaac4f142d8075f85'); // MS_DISCOUNT ID
      }
    }
    
    // Check spaceSavings array
    if (eventData && eventData.spaceSavings && Array.isArray(eventData.spaceSavings)) {
      const matchingSavings = eventData.spaceSavings.find(ss => {
        const chargingType = ss.chargingType?.toLowerCase() || '';
        return chargingType === (selectedChargingTypeValue || '').toLowerCase();
      });
      
      if (matchingSavings && matchingSavings.savings && Array.isArray(matchingSavings.savings)) {
        return matchingSavings.savings.some(savingItem => {
          const saving = savingItem.saving;
          return saving && saving.name === 'Deal' && saving.amountType === 'PERCENTAGE';
        });
      }
    }
    
    return false;
  }, [spaceDetails, form]);
  
  // Auto-fill booking value from charging option rate
  useEffect(() => {
    if (selectedChargingOption && !form.getFieldValue('bookingValue')) {
      const rate = selectedChargingOption.hourlyRate || 
                  selectedChargingOption.dailyRate || 
                  selectedChargingOption.weeklyRate || 
                  selectedChargingOption.monthlyRate || 
                  selectedChargingOption.blockRate || 0;
      
      if (rate > 0) {
        form.setFieldsValue({ bookingValue: rate });
      }
    }
  }, [selectedChargingOption, form]);
  
  return (
    <div className="financial-details-form">
      <Divider orientation="left">Financial Details</Divider>
      
      {spaceDetails && (
        <Card size="small" className="auto-fill-info">
          <Row>
            <Col span={24}>
              <InfoCircleOutlined className="info-icon" />
              {editMode ? (
                <strong>Edit Mode:</strong>
              ) : (
                <strong>Auto-loaded from space:</strong>
              )}
              {editMode ? (
                <span> Showing previously saved values. You can adjust any field as needed.</span>
              ) : (
                <>
                  <span> Base rate, commission, and discount have been pre-filled. </span>
                  {(hasSpaceHostDiscount || hasSpaceMsDiscount) && (
                    <span style={{ color: '#1890ff', fontWeight: 500 }}>
                      Discounts from space configuration are locked and cannot be changed.
                    </span>
                  )}
                  {(!hasSpaceHostDiscount && !hasSpaceMsDiscount) && (
                    <span>You can adjust discounts as needed.</span>
                  )}
                </>
              )}
            </Col>
          </Row>
        </Card>
      )}

      {/* NEW: Price Adjustment Warning Alert */}
      {calculatedValues.isPriceAdjusted && (
        <Alert
          message="Manual Price Adjustment Detected"
          description={
            <div>
              <p><strong>Original System Price:</strong> {calculatedValues.originalCalculatedPrice} LKR</p>
              <p><strong>Current Adjusted Price:</strong> {form.getFieldValue('bookingValue')} LKR</p>
              <p style={{
                color: calculatedValues.priceAdjustmentAmount >= 0 ? 'green' : 'red',
                fontWeight: 'bold'
              }}>
                <strong>Adjustment:</strong> {calculatedValues.priceAdjustmentAmount >= 0 ? '+' : ''}{calculatedValues.priceAdjustmentAmount} LKR 
                ({calculatedValues.priceAdjustmentPercent >= 0 ? '+' : ''}{calculatedValues.priceAdjustmentPercent.toFixed(2)}%)
              </p>
              <p style={{marginTop: 8, fontSize: '12px', color: '#666'}}>
                <WarningOutlined style={{marginRight: 4}} />
                This adjusted price will be used in all confirmations, receipts, and emails instead of the system-calculated price.
              </p>
            </div>
          }
          type="warning"
          showIcon
          style={{marginBottom: 16}}
        />
      )}

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name="bookingValue"
            label={
              <span>
                Booking Value (A)
                <Tooltip title="Base booking value before any discounts. Manually editing this will override system calculations.">
                  <InfoCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </span>
            }
            rules={[
              { required: true, message: 'Please enter booking value' },
              { type: 'number', min: 0, message: 'Must be greater than 0' }
            ]}
          >
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter booking value"
              min={0}
              precision={2}
            />
          </Form.Item>
        </Col>

        {/* Host Discounts - Simple repeated fields */}
        {hostDiscounts.length > 1 ? (
          <>
            {hostDiscounts.map((discount, index) => {
              // Generate descriptive label based on duration
              const getDurationLabel = () => {
                const chargingTypeValue = form.getFieldValue('selectedChargingType') || 'daily';
                const termNumber = index + 1;
                
                switch(chargingTypeValue) {
                  case 'hourly':
                    return termNumber === 1 ? '1st Hour' : termNumber === 2 ? '2nd Hour' : termNumber === 3 ? '3rd Hour' : `${termNumber}th Hour`;
                  case 'daily':
                    return termNumber === 1 ? '1st Day' : termNumber === 2 ? '2nd Day' : termNumber === 3 ? '3rd Day' : `${termNumber}th Day`;
                  case 'weekly':
                    return termNumber === 1 ? '1st Week' : termNumber === 2 ? '2nd Week' : termNumber === 3 ? '3rd Week' : `${termNumber}th Week`;
                  case 'monthly':
                    return termNumber === 1 ? '1st Month' : termNumber === 2 ? '2nd Month' : termNumber === 3 ? '3rd Month' : `${termNumber}th Month`;
                  default:
                    return `Term ${termNumber}`;
                }
              };
              
              const label = discount.name 
                ? `${discount.name} - ${getDurationLabel()} (%)` 
                : `Host Discount - ${getDurationLabel()} (%)`;
              
              return (
                <Col span={8} key={`host-${index}`}>
                  <Form.Item label={label}>
                    <InputNumber 
                      style={{ width: '100%' }} 
                      value={discount.percentage || 0}
                      disabled
                      min={0}
                      max={100}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
              );
            })}
            <Col span={8}>
              <Form.Item label="Total Host Discount Value (C)">
                <Input 
                  value={`LKR ${totalHostDiscountValue.toFixed(2)}`} 
                  disabled 
                  className="calculated-field"
                />
              </Form.Item>
            </Col>
          </>
        ) : (
          <>
            <Col span={8}>
              <Form.Item
                name="hostDiscountPercent"
                label={
                  <span>
                    Host Discount % (B)
                    {(hasSpaceHostDiscount || hasSpaceMsDiscount) && !editMode && (
                      <Tooltip title="This discount is configured in space settings and cannot be changed">
                        <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} />
                      </Tooltip>
                    )}
                  </span>
                }
                rules={[
                  { type: 'number', min: 0, max: 100, message: 'Please enter a valid percentage between 0 and 100' }
                ]}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={(hasSpaceHostDiscount || hasSpaceMsDiscount) && !editMode ? "Auto-calculated from space" : "Enter host discount %"}
                  min={0}
                  max={100}
                  precision={2}
                  disabled={(hasSpaceHostDiscount || hasSpaceMsDiscount) && !editMode}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Host Discount Value (C)">
                <Input 
                  value={`LKR ${calculatedValues.hostDiscountValue}`} 
                  disabled 
                  className="calculated-field"
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item label="Booking After Host Discount (D)">
            <Input 
              value={`LKR ${calculatedValues.bookingAfterHostDiscount}`} 
              disabled 
              className="calculated-field"
            />
          </Form.Item>
        </Col>

        {/* MS Discounts - Simple repeated fields */}
        {msDiscounts.length > 1 ? (
          <>
            {msDiscounts.map((discount, index) => {
              // Generate descriptive label based on duration
              const getDurationLabel = () => {
                const chargingTypeValue = form.getFieldValue('selectedChargingType') || 'daily';
                const termNumber = index + 1;
                
                switch(chargingTypeValue) {
                  case 'hourly':
                    return `${termNumber}th Hour`;
                  case 'daily':
                    return `${termNumber}th Day`;
                  case 'weekly':
                    return `${termNumber}th Week`;
                  case 'monthly':
                    return `${termNumber}th Month`;
                  default:
                    return `Term ${termNumber}`;
                }
              };
              
              const label = discount.name 
                ? `${discount.name} - ${getDurationLabel()} (%)` 
                : `MS Discount - ${getDurationLabel()} (%)`;
              
              return (
                <Col span={8} key={`ms-${index}`}>
                  <Form.Item label={label}>
                    <InputNumber 
                      style={{ width: '100%' }} 
                      value={discount.percentage || 0}
                      disabled
                      min={0}
                      max={100}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
              );
            })}
            <Col span={8}>
              <Form.Item label="Total MS Discount Value (F)">
                <Input 
                  value={`LKR ${totalMsDiscountValue.toFixed(2)}`} 
                  disabled 
                  className="calculated-field"
                />
              </Form.Item>
            </Col>
          </>
        ) : (
          <>
            <Col span={8}>
              <Form.Item
                name="msDiscountPercent"
                label={
                  <span>
                    MS Discount % (E)
                    {(hasSpaceHostDiscount || hasSpaceMsDiscount) && !editMode && (
                      <Tooltip title="This discount is configured in space settings and cannot be changed">
                        <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} />
                      </Tooltip>
                    )}
                  </span>
                }
                rules={[
                  { type: 'number', min: 0, max: 100, message: 'Please enter a valid percentage between 0 and 100' }
                ]}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={(hasSpaceHostDiscount || hasSpaceMsDiscount) && !editMode ? "Auto-calculated from space" : "Enter MS discount %"}
                  min={0}
                  max={100}
                  precision={2}
                  disabled={(hasSpaceHostDiscount || hasSpaceMsDiscount) && !editMode}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="MS Discount Value (F)">
                <Input 
                  value={`LKR ${calculatedValues.msDiscountValue}`} 
                  disabled 
                  className="calculated-field"
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item label={"Booking After All Discounts (G)"}>
            <Input 
              value={`LKR ${calculatedValues.bookingAfterAllDiscounts}`} 
              disabled 
              className="calculated-field"
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item 
            name="taxAmount"
            label={
              <span>
                Tax Amount (H)
                <Tooltip title="Tax amount (enter the actual tax value)">
                  <InfoCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </span>
            }
            rules={[
              { type: 'number', min: 0, message: 'Must be greater than or equal to 0' }
            ]}
          >
            {/* Temporarily disabled - Tax field is not in use currently */}
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter tax amount"
              min={0}
              precision={2}
              disabled
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="Final Booking Value (I)">
            <Input 
              value={`LKR ${calculatedValues.finalBookingValue}`} 
              disabled 
              className="calculated-field highlighted"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name="commissionPercent"
            label={
              <span>
                Commission % (J)
                <Tooltip title="Base commission percentage">
                  <InfoCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </span>
            }
            rules={[
              { type: 'number', min: 0, max: 100, message: 'Please enter a valid percentage between 0 and 100' }
            ]}
          >
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter commission %"
              min={0}
              max={100}
              precision={2}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="Base Commission Value (K)">
            <Input 
              value={`LKR ${calculatedValues.baseCommission}`} 
              disabled 
              className="calculated-field"
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="Payment to Host (L)">
            <Input 
              value={`LKR ${calculatedValues.paymentToHost}`} 
              disabled 
              className="calculated-field highlighted"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name="ccChargePercent"
            label={
              <span>
                Credit Card Charge % (M)
                <Tooltip title="Credit card processing fee percentage">
                  <InfoCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </span>
            }
            rules={[
              { type: 'number', min: 0, max: 100, message: 'Please enter a valid percentage between 0 and 100' }
            ]}
          >
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="Enter CC charge %"
              min={0}
              max={100}
              precision={2}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="CC Charge Value (N)">
            <Input 
              value={`LKR ${calculatedValues.ccChargeValue}`} 
              disabled 
              className="calculated-field"
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item label="Final Commission to MS (O)">
            <Input 
              value={`LKR ${calculatedValues.finalCommissionToMS}`} 
              disabled 
              className="calculated-field highlighted success"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* NEW: Price Adjustment Reason Field */}
      {calculatedValues.isPriceAdjusted && (
        <Row gutter={24} style={{marginTop: 16}}>
          <Col span={24}>
            <Card 
              size="small" 
              style={{
                backgroundColor: '#fff7e6',
                borderColor: '#ffa940'
              }}
            >
              <Form.Item
                name="priceAdjustmentReason"
                label={
                  <span style={{fontWeight: 'bold'}}>
                    <WarningOutlined style={{marginRight: 8, color: '#fa8c16'}} />
                    Price Adjustment Reason (Required)
                  </span>
                }
                rules={[
                  { required: true, message: 'Please provide a reason for manual price adjustment' },
                  { min: 10, message: 'Reason must be at least 10 characters' }
                ]}
                extra="Provide a clear explanation for why the price was manually adjusted. This will be logged for audit purposes."
              >
                <Input.TextArea 
                  rows={3} 
                  placeholder="e.g., Special bulk booking discount negotiated with client, VIP customer pricing, promotional offer, etc."
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default FinancialDetailsForm;
